import { useEffect, useState } from "react";
import { Copy, Download, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, ts } from "@/lib/api";
import { EdiTag } from "@/components/EdiTag";
import { StatusBadge } from "@/components/StatusBadge";

const Seg = ({ line }) => {
  const parts = line.replace(/~$/, "").split("*");
  return (
    <div className="whitespace-pre-wrap break-all">
      <span className="font-semibold text-amber-300">{parts[0]}</span>
      {parts.slice(1).map((p, i) => (
        <span key={i}>
          <span className="text-slate-600">*</span>
          <span className="text-slate-200">{p}</span>
        </span>
      ))}
      <span className="text-slate-600">~</span>
    </div>
  );
};

export const RawEdiDialog = ({ messageId, open, onOpenChange, onChanged }) => {
  const [m, setM] = useState(null);

  useEffect(() => {
    if (open && messageId) api.get(`/edi/messages/${messageId}`).then((r) => setM(r.data));
    if (!open) setM(null);
  }, [open, messageId]);

  const lines = (m?.raw || "").replace(/\r/g, "").split(/~\s*/).filter(Boolean);
  const copy = () => navigator.clipboard?.writeText(m.raw).then(() => toast.success("Copied to clipboard"));
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([m.raw], { type: "text/plain" }));
    a.download = `${m.doc_type}_${m.control || m.id}.edi`;
    a.click();
  };
  const review = async (decision) => {
    try {
      const r = await api.post(`/edi/messages/${m.id}/review`, { decision, note: "" });
      setM({ ...m, ...r.data });
      toast.success(decision === "confirmed" ? "Output confirmed" : "Output flagged");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const transmit = async () => {
    try {
      const r = await api.post(`/edi/messages/${m.id}/transmit`);
      setM({ ...m, ...r.data });
      r.data.status === "error" ? toast.error(r.data.error || "Send failed") : toast.success(r.data.error?.startsWith("MOCKED") ? "Marked as sent (AS2 off - MOCKED)" : "Sent to Group O via AS2");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl border-slate-800 bg-[#1F232C] text-slate-100" data-testid="raw-edi-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex flex-wrap items-center gap-3">
            {m && <EdiTag code={m.doc_type} />} · {m?.direction === "out" ? "Outbound" : "Inbound"}
            {m && <StatusBadge status={m.status} />}
          </DialogTitle>
        </DialogHeader>
        {m && (
          <>
            <div className="grid grid-cols-2 gap-3 font-mono text-xs text-slate-400 md:grid-cols-4">
              <div>Control <span className="block text-slate-200">{m.control || "—"}</span></div>
              <div>Partner <span className="block text-slate-200">{m.partner}</span></div>
              <div>Created <span className="block text-slate-200">{ts(m.created_at)}</span></div>
              <div>Segments <span className="block text-slate-200">{lines.length}</span></div>
              {m.direction === "out" && m.doc_type !== "997" && (
                <div>997 ack <span className="block" data-testid="raw-edi-ack"><StatusBadge status={m.ack_status || "awaiting_997"} /></span></div>
              )}
              {m.direction === "out" && (
                <div>Review <span className="block" data-testid="raw-edi-review"><StatusBadge status={`review_${m.review || "pending"}`} /></span></div>
              )}
              {m.attempts > 0 && <div>Attempts <span className="block text-slate-200">{m.attempts}</span></div>}
            </div>
            {m.error && (
              <div data-testid="raw-edi-error" className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                {m.error}
              </div>
            )}
            <div data-testid="raw-edi-content" className="max-h-[50vh] overflow-auto rounded-sm border border-slate-800 bg-[#111317] p-4 font-mono text-xs leading-6">
              {lines.map((l, i) => (
                <Seg key={i} line={l} />
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" onClick={copy} data-testid="raw-edi-copy-btn" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={download} data-testid="raw-edi-download-btn" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download
              </Button>
              {m.direction === "out" && m.review !== "flagged" && (
                <Button variant="outline" size="sm" onClick={() => review("flagged")} data-testid="raw-edi-flag-btn" className="border-rose-500/40 bg-transparent text-rose-300 hover:bg-rose-500/10 hover:text-rose-200">Flag issue</Button>
              )}
              {m.direction === "out" && m.review !== "confirmed" && (
                <Button size="sm" onClick={() => review("confirmed")} data-testid="raw-edi-confirm-btn" className="bg-emerald-500 text-white hover:bg-emerald-600">Confirm output</Button>
              )}
              {m.direction === "out" && !m.test && ["ready", "error"].includes(m.status) && (
                <Button size="sm" onClick={transmit} data-testid="raw-edi-transmit-btn" className="bg-emerald-500 text-white hover:bg-emerald-600">
                  <Send className="mr-1.5 h-3.5 w-3.5" /> {m.status === "error" ? "Resend" : "Transmit"}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
